import {
  afterNextRender,
  Component,
  computed,
  OnDestroy,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BrowserStorageService } from './service/browser-storage.service';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ToolbarComponent } from './shared-components/toolbar/toolbar.component';
import { PublicToolbarComponent } from './shared-components/toolbar/public-toolbar.component';
import { AuthService } from './service/auth.service';
import { AnalyticsService } from './service/analytics.service';
import { MetaService } from './service/meta.service';
import { ThemeService } from './service/theme.service';
import { environment } from '../environments/environment';
import {
  TourAnchorMatMenuDirective,
  TourStepTemplateComponent,
} from 'ngx-ui-tour-md-menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { SidenavService } from './service/sidenav.service';
import { filter, Subscription } from 'rxjs';

import { UserResponse } from '../../api-gen/gen/models/user-response';
import { FeatureToggleService } from './service/feature-toggle.service';
import { MatDialog } from '@angular/material/dialog';
import { NewBadgeComponent } from './shared-components/new-badge/new-badge.component';
import { VideoHowToComponent } from './shared-components/toolbar/video-how-to/video-how-to.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SocialIconRegistryService } from './service/social-icon-registry.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ToolbarComponent,
    PublicToolbarComponent,
    TourStepTemplateComponent,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDividerModule,
    RouterLink,
    NewBadgeComponent,
    TourAnchorMatMenuDirective,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private analytics = inject(AnalyticsService);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  sidenavService = inject(SidenavService);
  featureToggleService = inject(FeatureToggleService);
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);
  private metaService = inject(MetaService);
  private socialIcons = inject(SocialIconRegistryService);
  private titleService = inject(Title);
  private storage = inject(BrowserStorageService);

  title = 'aiavatar';

  // Signal to track current route
  currentRoute = signal<string>('');

  // Computed signal to check if on home page
  hideSideNav = computed(
    () =>
      this.currentRoute() === '/' ||
      this.currentRoute() === '/login' ||
      this.currentRoute() === '/register',
  );

  // Signal for current user
  currentUser = signal<UserResponse | undefined>(undefined);

  // Responsive signals
  isMobile = signal<boolean>(false); // < 1000px
  isCompact = signal<boolean>(false); // 1000px - 1758px
  isDesktop = computed(() => !this.isMobile() && !this.isCompact()); // >= 1758px

  // Desktop sidenav expanded/collapsed state
  sidenavExpanded = signal<boolean>(true);

  // localStorage key for sidenav state
  private readonly SIDENAV_STATE_KEY = 'sidenav-expanded-state';

  // Computed signal for sidenav mode
  sidenavMode = computed(() => {
    // In compact mode, switch to 'over' mode when drawer is opened
    if (this.isCompact() && this.sidenavService.opened()) {
      return 'over';
    }
    // Mobile always uses 'over' mode
    if (this.isMobile()) {
      return 'over';
    }
    // Desktop and compact (when drawer closed) use 'side' mode
    return 'side';
  });

  // Computed signal for sidenav opened state
  sidenavOpened = computed(() => {
    if (this.hideSideNav()) return false;
    // Mobile: controlled by sidenavService
    if (this.isMobile()) return this.sidenavService.opened();
    // Compact: always show side nav (compact), or drawer when opened
    if (this.isCompact()) return true;
    // Desktop: always open
    return true;
  });

  private subscriptions: Subscription[] = [];

  protected readonly environment = environment;

  constructor() {
    const authService = this.authService;

    authService.initializeUser();

    if (environment.site === 'ECAF') {
      this.themeService.setTheme('dark');
      // set title (Title service is SSR-safe, unlike document.title)
      this.titleService.setTitle('NakedAI');
    }

    // Browser-only startup work: referrer/UTM capture and affiliate-id URL
    // cleanup depend on window/document and must not run during SSR.
    afterNextRender(() => {
      const referrer = document.referrer;
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      const utmCampaign = params.get('utm_campaign');
      const affiliateId = params.get('affiliate_id');
      this.analytics.storeUserSourceData(
        referrer,
        utmSource,
        utmCampaign,
        affiliateId,
      );

      if (affiliateId) {
        params.delete('affiliate_id');
        const query = params.toString();
        const path =
          window.location.pathname +
          (query ? '?' + query : '') +
          window.location.hash;
        window.history.replaceState(null, '', path);
      }

      // Restore sidenav state from localStorage (only for desktop).
      // Done after first render so server and client render identically.
      const savedState = this.storage.getItem(this.SIDENAV_STATE_KEY);
      if (savedState !== null) {
        this.sidenavExpanded.set(savedState === 'true');
      }
    });

    // Subscribe to current user
    this.subscriptions.push(
      authService.getCurrentUser().subscribe((currentUser) => {
        this.currentUser.set(currentUser);
      }),
    );
  }

  ngOnInit(): void {
    this.socialIcons.register();

    // Track route changes to detect home page
    this.currentRoute.set(this.router.url);

    this.metaService.reset();

    // Set canonical URL on initial load (strips query params).
    // MetaService prepends the canonical origin from the environment,
    // so this works during SSR/prerendering too.
    this.metaService.updateCanonicalUrl(this.router.url);

    this.subscriptions.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.currentRoute.set(event.urlAfterRedirects);
          this.metaService.updateCanonicalUrl(event.urlAfterRedirects);
          this.applyRouteMeta();
          // Close sidenav on mobile/compact when navigating to home page
          if (this.hideSideNav() && (this.isMobile() || this.isCompact())) {
            this.sidenavService.close();
          }
        }),
    );

    // Observe screen size changes for mobile and compact modes
    this.subscriptions.push(
      this.breakpointObserver
        .observe([
          '(max-width: 1320px)',
          '(min-width: 1321px) and (max-width: 2056px)',
        ])
        .subscribe((result) => {
          const isMobileNow = result.breakpoints['(max-width: 1320px)'];
          const isCompactNow =
            result.breakpoints['(min-width: 1321px) and (max-width: 2056px)'];

          this.isMobile.set(isMobileNow);
          this.isCompact.set(isCompactNow);

          // Close drawer when switching to mobile or compact
          if (isMobileNow || isCompactNow) {
            this.sidenavService.close();
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Applies title/description declared on the active route (`data.meta`).
   * Runs on every NavigationEnd, including the initial SSR/prerender pass,
   * so prerendered HTML contains the correct per-page tags.
   *
   * Routes are handled as follows:
   * - `data.meta` present: applied here, branded with the site domain.
   * - `data.selfMeta` true: the component manages its own meta — hands off.
   * - neither: reset to the site-wide defaults.
   */
  private applyRouteMeta(): void {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }

    const meta = snapshot.data['meta'] as
      | { title: string; description: string }
      | undefined;

    if (meta) {
      this.metaService.update({
        title: `${meta.title} | ${environment.domain}`,
        description: meta.description,
      });
    } else if (!snapshot.data['selfMeta']) {
      this.metaService.reset();
    }
  }

  onLogout(): void {
    this.authService.logout();
    this.sidenavService.close();
  }

  onDiscord(): void {
    window.open('https://discord.gg/PNMsFw7eMN', '_blank');
  }

  onCreateVideoClicked(): void {
    this.dialog
      .open(VideoHowToComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.router.navigate(['/gallery']);
        }
      });
    this.sidenavService.close();
  }

  toggleSidenavExpanded(): void {
    this.sidenavExpanded.update((value) => {
      const newValue = !value;
      // Only save to localStorage in full desktop mode (>= 1758px)
      if (this.isDesktop()) {
        this.storage.setItem(this.SIDENAV_STATE_KEY, String(newValue));
      }
      return newValue;
    });
  }

  onSidenavOpenedChange(opened: boolean, sidenav: any): void {
    // Mobile mode: sync state with service
    if (this.isMobile()) {
      if (opened) {
        this.sidenavService.open();
      } else {
        this.sidenavService.close();
      }
      return;
    }

    // Compact mode: when drawer closes via backdrop
    if (this.isCompact() && !opened && this.sidenavService.opened()) {
      // Update service state to close the drawer
      this.sidenavService.close();
      // This changes mode from 'over' to 'side'
      // Now manually reopen the sidenav in 'side' mode to show compact view
      setTimeout(() => {
        sidenav.open();
      }, 0);
    }
  }
}
